import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GradeIcon from "@mui/icons-material/Grade";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Avatar,
  Box,
  CssBaseline,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { MaviTrackrLogo } from "assets/images";
import ScoreDisplay from "components/ScoreDisplay";
import { AuthContext } from "navigation/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { AppBar, Drawer, DrawerHeader, StyledMenu } from "./SidebarStyles";
import { navLinksMenu, navLinksSettings } from "./navLinks";

export default function Sidebar({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleViewScore = () => {
    setOpenDialog(true);
  };

  const handleCloseViewScore = () => {
    setOpenDialog(false);
  };

  const isActive = (route: string) => {
    return location.pathname.includes(route);
  };

  const signOut = () => {
    logout();
  };

  const userRole = user.user.roles[0].name;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ backgroundColor: "white" }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
              color: "black",
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ color: "black", opacity: 0.7, fontWeight: 800 }}
            >
              Hello {user.user.first_name}
            </Typography>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontSize: "14px",
                color: "black",
                opacity: 0.8,
                fontWeight: 500,
              }}
            >
              Welcome to your PM Dashboard
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              padding: "6px 8px",
              borderRadius: "20px",
              cursor: "pointer",
            })}
          >
            <Avatar
              src={user.user.profile_image}
              alt={`${user.user.first_name} ${user.user.last_name}`}
              sx={{ width: 30, height: 30, marginRight: "6px" }}
            />
            <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 600, marginRight: "4px" }}>
              {user.user.first_name}
            </Typography>
            <ExpandMoreIcon onClick={(event) => setMenuAnchor(event.target as any)} />
          </Box>
          <StyledMenu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
            <MenuItem sx={{ display: "flex", alignItems: "center" }} onClick={handleViewScore}>
              <GradeIcon fontSize="large" />
              <Typography variant="h6" fontSize="16px">
                View Score
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem sx={{ display: "flex", alignItems: "center" }} onClick={signOut}>
              <MeetingRoomIcon fontSize="large" />
              <Typography variant="h6" fontSize="16px" color="tomato">
                Logout
              </Typography>
            </MenuItem>
          </StyledMenu>

          {/* Score Dialog */}

          <Dialog open={openDialog} onClose={handleCloseViewScore}>
            <DialogContent>
              <ScoreDisplay onClose={handleCloseViewScore} />
            </DialogContent>
          </Dialog>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img src={MaviTrackrLogo} alt="Mavitrackr Logo" width={70} height={70} />
          <Typography
            variant="h5"
            noWrap
            component="div"
            sx={{
              color: "black",
              fontWeight: 800,
              display: open ? "block" : "none",
            }}
          >
            Mavitrackr
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <List>
            {navLinksMenu.map(({ text, icon, route, roles }) => {
              return (
                roles.includes(userRole) && (
                  <ListItem
                    key={text}
                    sx={{
                      display: "block",
                      padding: "2px",
                      backgroundColor: isActive(route) ? theme.palette.primary.main : "transparent",
                    }}
                    onClick={() => navigate(route)}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                          color: isActive(route) ? "white" : theme.palette.primary.main,
                        }}
                      >
                        {icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            component="span"
                            variant="body1"
                            sx={{
                              opacity: open ? 1 : 0,
                              color: isActive(route) ? "white" : theme.palette.primary.main,
                              fontWeight: isActive(route) ? "bold" : "normal",
                            }}
                          >
                            {text}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )
              );
            })}
          </List>
          <List>
            {navLinksSettings.map(({ text, icon, route }) => (
              <ListItem
                key={text}
                disablePadding
                sx={{
                  display: "block",
                  backgroundColor: isActive(route) ? theme.palette.primary.main : "transparent",
                }}
                onClick={() => navigate(route)}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: isActive(route) ? "white" : theme.palette.primary.main,
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          opacity: open ? 1 : 0,
                          color: isActive(route) ? "white" : theme.palette.primary.main,
                          fontWeight: isActive(route) ? "bold" : "normal",
                        }}
                      >
                        {text}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}
